<script>
import FlowChart from 'flowchart-vue';
import useValidation from "@/use/Validation";
import {computed, onMounted, ref} from "vue";
import useSharedData from "@/use/SharedData";
import useTools from "@/use/Tools";
import useSharedMethod from "@/use/SharedMethod";
import {useWorkFlowStore} from "@/stores/Request/WorkFlow";
import axios from "axios";

export default {
    components: {FlowChart},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },
    setup(props) {
        const {mxRules} = useValidation();
        const readonly = computed(() => props.deleteAction || props.viewAction);
        const {loading, overlay, dialog} = useSharedData();
        const {$route, $refs, $i18n} = useTools();
        const {getBack} = useSharedMethod();
        const workFlowStore = useWorkFlowStore();

        const stepStates = [
            {id: "INIT", name: $i18n.t("validation_work_flow.INIT")},
            {id: "MIDDLE", name: $i18n.t("validation_work_flow.MIDDLE")},
            {id: "FINAL", name: $i18n.t("validation_work_flow.FINAL")},
        ];
        const modules = [
            {id: "ABSENCE_REQUEST", name: $i18n.t("module.absence_request")},
            {id: "DEBT", name: $i18n.t("module.debts")},
            {id: "CLAIM", name: $i18n.t("module.claim")},
            {id: "ADMIN_REQUEST", name: $i18n.t("module.admin_request")},
        ];

        const workFlow = ref({steps: []});
        const nodes = ref([]);
        const connections = ref([]);

        function findById(id) {
            loading.value = true;
            axios.get(`/validationWorkFlows/${id}`).then((response) => {
                workFlow.value = response.data;
                buildFlowChart();
            }).finally(() => {
                loading.value = false;
            });
        }

        function buildFlowChart() {
            workFlow.value.steps.forEach((step) => {
                nodes.value.push({
                    id: step.id,
                    x: step.x,
                    y: step.y,
                    name: step.name,
                    type: "operation",
                    width: step.width,
                    height: step.height,
                    approvers: [
                        {
                            id: step.state,
                            name: $i18n.t(`validation_work_flow.${step.state}`),
                        },
                    ],
                });
                if (step.successNextStepId) {
                    connections.value.push({
                        source: {
                            id: step.id,
                            position: step.successSourceLinkPosition,
                        },
                        destination: {
                            id: step.successNextStepId,
                            position: step.successDestinationLinkPosition,
                        },
                        id: +new Date(),
                        type: "pass",
                    });
                }
                if (step.faildNextStepId) {
                    connections.value.push({
                        source: {id: step.id, position: step.faildSourceLinkPosition},
                        destination: {
                            id: step.faildNextStepId,
                            position: step.faildDestinationLinkPosition,
                        },
                        id: +new Date(),
                        type: "reject",
                    });
                }
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                $refs.chart.save();
                axios.post('/validationWorkFlows', workFlow.value).then((response) => {
                    workFlowStore.addWorkFlow(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                $refs.chart.save();
                axios.put('/validationWorkFlows', workFlow.value).then((response) => {
                    workFlowStore.updateWorkFlow(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/validationWorkFlows/${workFlow.value.id}`).then(() => {
                workFlowStore.deleteWorkFlow(workFlow.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        const editNodeDialog = ref(false);
        const selectedNode = ref({});
        const state = ref({});

        function handleEditNode(node) {
            editNodeDialog.value = true;
            selectedNode.value = node;
            state.value = node.approvers[0].id;
        }

        function closeNodeDialog() {
            editNodeDialog.value = false;
            selectedNode.value = {};
            state.value = null;
        }


        const editConnectionDialog = ref(false);
        const selectedConnection = ref({});

        function handleEditConnection(connection) {
            editConnectionDialog.value = true;
            selectedConnection.value = connection;
        }

        function changeState() {
            selectedNode.value.approvers[0] = {
                id: state.value,
                name: $i18n.t(`validation_work_flow.${state.value}`),
            };
        }

        function closeConnectionDialog() {
            editConnectionDialog.value = false;
        }

        function handleChartSave(nodes, connections) {
            var steps = [];
            nodes.forEach((node) => {
                steps.push({
                    id: node.id,
                    name: node.name,
                    x: node.x,
                    y: node.y,
                    height: node.height,
                    width: node.width,
                    state: node.approvers[0].id,
                });
            });
            connections.forEach((connection) => {
                var step_source = steps.find((el) => el.id === connection.source.id);
                var step_destination = steps.find((el) => el.id === connection.destination.id);
                if (connection.type === "pass") {
                    step_source.successSourceLinkPosition = connection.source.position;
                    step_source.successDestinationLinkPosition =
                        connection.destination.position;
                    step_source.successNextStepName = step_destination.name;
                } else if (connection.type === "reject") {
                    step_source.faildSourceLinkPosition = connection.source.position;
                    step_source.faildDestinationLinkPosition = connection.destination.position;
                    step_source.faildNextStepName = step_destination.name;
                }
            });
            workFlow.value.steps = steps;
        }

        function handleDblClick(position) {
            $refs.chart.add({
                id: +new Date(),
                x: position.x,
                y: position.y,
                name: "New",
                type: "operation",
                width: 200,
                height: 70,
                approvers: [
                    {
                        id: "MIDDLE",
                        name: $i18n.t("validation_work_flow.MIDDLE"),
                    },
                ],
            });
        }


        onMounted(() => {
            if (!props.addAction)
                findById($route.params.id);
        });

        return {
            mxRules,
            readonly,
            loading,
            overlay,
            dialog,
            stepStates,
            modules,
            workFlow,
            nodes,
            connections,
            selectedNode,
            editNodeDialog,
            state,
            handleEditNode,
            changeState,
            closeNodeDialog,
            editConnectionDialog,
            selectedConnection,
            handleEditConnection,
            closeConnectionDialog,
            handleChartSave,
            handleDblClick,
            getBack,
            save,
            update,
            remove
        }
    }
};
</script>

<template>
    <div>
        <v-dialog
            transition="dialog-bottom-transition"
            v-model="editNodeDialog"
            max-width="400"
        >
            <v-card>
                <v-card-title>{{ $t("common.edit") }}</v-card-title>
                <v-divider/>
                <v-card-text>
                    <v-container fluid>
                        <v-row>
                            <v-col cols="12">
                                <w-text-field
                                    v-model="selectedNode.name"
                                    :label="$t('common.name')"
                                />
                            </v-col>
                            <v-col cols="6">
                                <w-text-field
                                    v-model="selectedNode.height"
                                    :label="$t('common.name')"
                                    type="number"
                                    min="50"
                                />
                            </v-col>
                            <v-col cols="6">
                                <w-text-field
                                    v-model="selectedNode.width"
                                    :label="$t('common.name')"
                                    type="number"
                                    min="50"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-enum-select
                                    v-model="state"
                                    :label="'Etat *'"
                                    :items="stepStates"
                                    @input="changeState"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn
                        text
                        color="teal"
                        @click="closeNodeDialog"
                    >
                        OK
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog
            transition="dialog-bottom-transition"
            v-model="editConnectionDialog"
            max-width="400"
        >
            <v-card>
                <v-card-title>{{ $t("common.edit") }}</v-card-title>
                <v-divider/>
                <v-card-text>
                    <v-row>
                        <v-col cols="12">
                            <v-radio-group v-model="selectedConnection.type" row>
                                <v-radio :value="'pass'" color="teal" label="Accpté"/>
                                <v-radio :value="'reject'" color="red" label="Réfuse"/>
                            </v-radio-group>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn text color="teal" @click="closeConnectionDialog">
                        OK
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            transition="dialog-bottom-transition"
            v-model="dialog"
            max-width="1400"
            scrollable
            persistent
        >
            <v-card>
                <w-dialog-toolbar
                    :add-action="addAction"
                    :update-action="updateAction"
                    :delete-action="deleteAction"
                    :view-action="viewAction"
                />
                <w-overlay
                    :overlay="overlay"
                    :loading="loading"
                    :delete-action="deleteAction"
                    @remove="remove"
                    @cancel="overlay = false"
                />
                <v-card-text>
                    <v-container fluid>
                        <v-form ref="form" lazy-validation>
                            <v-row dense>
                                <v-col cols="6">
                                    <w-text-field
                                        v-model="workFlow.name"
                                        :label="`${$t('common.name')} *`"
                                    />
                                </v-col>
                            </v-row>
                        </v-form>
                        <br/>
                        <flow-chart
                            :nodes="nodes"
                            :connections="connections"
                            :width="'100%'"
                            :readonly="readonly"
                            @editnode="handleEditNode"
                            @editconnection="handleEditConnection"
                            @dblclick="handleDblClick"
                            @save="handleChartSave"
                            ref="chart"
                        >
                        </flow-chart>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="getBack"/>
                    <w-btn-add
                        :show="addAction"
                        :loading="loading"
                        @press="save"
                    />
                    <w-btn-update
                        :show="updateAction"
                        :loading="loading"
                        @press="update"
                    />
                    <w-btn-delete
                        :show="deleteAction"
                        :loading="loading"
                        @press="overlay = true"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>1